import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { getTabList, Icon } from '@umijs/max'
import type { InitialStateTypes } from '@/utils/types'
import type { RunTimeLayoutConfig } from '@umijs/max';
import logger from './ali-logger';

// 注册阿里ARMS到window对象
window.aliLogger = logger;


import defaultSettings from '../config/defaultSettings';
import access from './access';
import { fetchConfig, queryCurrentUser } from './services/logic/login';
import umiRequest from './utils/umiRequest';
import { getLocalStorageItem, setLocalStorageItem, logoutToLogin, forceRedirect, initInitialState } from './utils';
import { LOCAL_STORAGE, ROUTES } from './utils/enums';
import { eq, get } from 'lodash-es';
import { BasicLayout } from '@/components/Business/BasicLayout';
import TabsLayout, { TabsLayoutProps } from '@/components/Business/TabsLayout';

import './global.less';
import { getDisputeReason } from './services/saas-dispute/allSessions';
import { fetchUserAPI } from './services/system';
import { getAccessToken } from './utils/common/authToken';
import { clearLocalStorageByModuleMapping } from './utils/utils';
import { getCountryLanguages } from './services/translate';
import { getTicketStatusCount } from './services/configs';

clearLocalStorageByModuleMapping()

/**
 *
 * @description umiUse-patchRoutes 路由配置修改
 * @export
 * @param {*} { routes }
 */
export function patchRoutes({ routes }: any) {
  for (let key in routes) {
    const icon = routes[key].icon;
    if (!icon) { continue; }
    if (icon?.includes('@/assets')) {
      const iconPath = icon.replace('@/assets', '')
      routes[key].icon = <img src={iconPath} width={16} height={16} />;
    } else {
      routes[key].icon = <Icon icon={icon} className="qd-text-base" style={{ fontSize: '1.2rem' }} />;
    }
  }
}

/**
 * @description 登录后根据用户权限，按菜单顺序返回第一个有权限的页面路径
 * 顺序与 config/router/index.ts 中 sortList 的菜单顺序保持一致
 */
const getAccessibleHome = (currentUser?: USERAPI.CurrentUser): string => {
  const permissions = access({ currentUser });
  const candidates: { path: string; accessKey?: keyof typeof permissions }[] = [
    { path: '/email-ticket/all', accessKey: 'email-ticket' },
    { path: '/saas-ticket/all', accessKey: 'chat-ticket' },
    { path: '/customer-center' }, // 无权限限制，作为兜底
  ];
  return candidates.find((item) => !item.accessKey || permissions[item.accessKey])?.path || ROUTES.HOME;
};

/**
 * @description 全局初始状态设置
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * @return initialState InitialStateTypes
 */
export async function getInitialState(): Promise<InitialStateTypes> {

  // 获取 LAYOUT 的值
  const LAYOUT_SETTINGS = defaultSettings;
  // 获取 ACCESS_TOKEN
  const ACCESS_TOKEN = getAccessToken() || undefined;


  // 存储到 localstorage
  setLocalStorageItem(LOCAL_STORAGE.LAYOUT, LAYOUT_SETTINGS)

  // 初始化数据
  const initialState: InitialStateTypes = {
    accessToken: ACCESS_TOKEN,
    settings: LAYOUT_SETTINGS as LayoutSettings,
    collapsed: true,
    fetchUserInfo: async () => await queryCurrentUser(),
    fetchEmailTicketStatusCount: async () => await getTicketStatusCount(),
    fetchDisputeReason: async () => await getDisputeReason(),
    forceCollapsed: true,
    languageMap: [],
    fetchUser: async (params: any = {}) => await fetchUserAPI(params),
    fetchCountryLanguages: async () => await getCountryLanguages(),
    // fetchConfigInfo: async () => get(await fetchConfig(), 'data', []),
  }

  if(location.pathname === ROUTES.CHAT_INVITATION || location.pathname.startsWith(`${ROUTES.CHAT_INVITATION}/`)) {
    return initialState
  }

  // 判断是否登录，没有登录跳转到登录页
  if (!ACCESS_TOKEN) {
    logoutToLogin();
    return initialState
  }

  // 判断在登录页是否已登录，已登录则根据权限跳转到第一个有权限的页面
  if (eq(location.pathname, ROUTES.LOGIN) && ACCESS_TOKEN) {
    let homePath: string = ROUTES.HOME;
    try {
      const currentUser = await queryCurrentUser();
      homePath = getAccessibleHome(currentUser);
    } catch (error) {
      // 获取用户信息失败时回退到默认首页
    }
    forceRedirect(homePath);
  }

  // 如果不是登录页面，执行
  if (!eq(location.pathname, ROUTES.LOGIN)) {
    return await initInitialState(initialState)
  }
  return initialState
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
/**
 *
 *
 * @param {*} {
 *   initialState,
 * }
 * @return {*}
 */
export const layout: RunTimeLayoutConfig = BasicLayout;

/**
 * @description: 完全覆盖内置的多 Tabs 组件，需要搭配配置 hasCustomTabs:true 使用。
 * @doc https://alitajs.com/zh-CN/docs/guides/tabs#getcustomtabs
 */
export const getCustomTabs = () => (props: TabsLayoutProps) => <TabsLayout {...props} />

/**
 * @description: request 配置，可以配置错误处理，它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = umiRequest;

export function onRouteChange() {
    window.lfq('track', 'pageView');
}
